import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";
import { api, errDetail, fmtDate, fmtUSD } from "@/lib/api";
import type { Booking } from "@/types";

function Row({ k, v, id }: { k: string; v: React.ReactNode; id?: string }) {
  return <div className="flex justify-between py-3 border-b border-slate-100 text-sm"><span className="text-slate-500">{k}</span><span data-testid={id} className="font-semibold text-[#0B192C] text-right">{v}</span></div>;
}

export default function Checkout() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const [params] = useSearchParams();
  const returned = params.get("return") === "1";
  const [paying, setPaying] = useState(false);
  const [waited, setWaited] = useState(0);
  const { data: cfg } = useQuery<{ live: boolean }>({ queryKey: ["payments-config"], queryFn: async () => (await api.get("/payments/config")).data });
  const live = cfg?.live ?? true;
  const confirming = returned && waited < 24;
  const { data: b, isLoading, refetch } = useQuery<Booking>({
    queryKey: ["booking", id], queryFn: async () => (await api.get(`/bookings/${id}`)).data,
    refetchInterval: (q) => (confirming && q.state.data?.status === "pending_payment" ? 2500 : false),
  });
  useEffect(() => {
    if (!confirming || b?.status !== "pending_payment") return;
    const tm = setTimeout(() => setWaited((w) => w + 1), 2500);
    return () => clearTimeout(tm);
  }, [confirming, b?.status, waited]);

  const pay = async () => {
    setPaying(true);
    try {
      if (live) {
        const { data } = await api.post(`/payments/checkout/${id}`);
        window.location.assign(data.checkout_url);
        return;
      }
      await api.post(`/payments/mock/${id}/confirm`);
    } catch (e) {
      const d = errDetail(e);
      if (d === "payment_expired") { await refetch(); return; }
      if (d !== "date_unavailable") toast.error(d === "payment_provider_error" ? t("co.providerError") : t("err.generic"));
    } finally {
      await refetch();
      setPaying(false);
    }
  };

  if (isLoading || !b) return <div className="py-40 grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-[#00A8A0]" /></div>;

  const charge = b?.charge_amount ?? b?.amount ?? 0;
  const isDeposit = !!b && charge < b.amount - 0.01;
  const summary = (
    <div className="mt-6">
      <Row k={t("co.ref")} v={b.id.slice(0, 8).toUpperCase()} id="checkout-ref" />
      <Row k={t("nav.yachts")} v={b.yacht_name} />
      <Row k={t("search.date")} v={fmtDate(b.date, lang)} />
      <Row k={t("book.block").replace(/^\d\.\s/, "")} v={t(`block.${b.block}`)} />
      <Row k={t("search.guests")} v={b.guests} />
      <Row k={t("book.total")} v={`${fmtUSD(b.amount)} USD`} id="checkout-amount" />
      {isDeposit && <Row k={b.status === "paid" ? t("co.depositPaid") : t("co.depositNow")} v={`${fmtUSD(charge)} USD`} id="checkout-deposit" />}
      {isDeposit && <Row k={t("co.balance")} v={`${fmtUSD(b.amount - charge)} USD`} id="checkout-balance" />}
    </div>
  );

  return (
    <div className="max-w-xl mx-auto px-5 py-16 sm:py-24" data-testid="checkout-page">
      <div className="bg-white rounded-xl border border-slate-200 shadow-[0_30px_80px_-40px_rgba(11,25,44,0.45)] p-6 sm:p-10">
        {b.status === "paid" ? (
          <div className="text-center" data-testid="checkout-paid">
            <CheckCircle2 className="mx-auto h-14 w-14 text-[#00A8A0]" />
            <h1 className="mt-4 font-display text-3xl text-[#0B192C]">{t("co.paidTitle")}</h1>
            <p className="mt-2 text-sm text-slate-600">{t("co.paidBody")}</p>
          </div>
        ) : b.status === "conflict" ? (
          <div className="text-center" data-testid="checkout-conflict">
            <AlertTriangle className="mx-auto h-14 w-14 text-amber-500" />
            <h1 className="mt-4 font-display text-3xl text-[#0B192C]">{t("co.conflictTitle")}</h1>
            <p className="mt-2 text-sm text-slate-600">{t("co.conflictBody")}</p>
          </div>
        ) : b.status === "expired" ? (
          <div className="text-center" data-testid="checkout-expired">
            <AlertTriangle className="mx-auto h-14 w-14 text-slate-400" />
            <h1 className="mt-4 font-display text-3xl text-[#0B192C]">{t("co.expiredTitle")}</h1>
            <p className="mt-2 text-sm text-slate-600">{t("co.expiredBody")}</p>
            <Link to={`/yachts/${b.yacht_id}`} data-testid="checkout-expired-rebook" className="mt-6 inline-flex rounded-full bg-[#0B192C] px-6 py-3 text-sm font-semibold text-white">{t("co.rebook")}</Link>
          </div>
        ) : b.status === "pending_payment" ? (
          <div>
            <p className="eyebrow flex items-center gap-2"><Lock className="h-3.5 w-3.5" />Whop</p>
            <h1 className="mt-3 font-display text-3xl text-[#0B192C]">{t("co.title")}</h1>
            <p data-testid="checkout-expiry-note" className="mt-3 text-xs text-slate-500">{t("co.expiryNote")}</p>
            {isDeposit && <p data-testid="checkout-deposit-note" className="mt-3 rounded-md bg-[#F3E5AB]/40 border border-[#D4AF37]/40 px-4 py-3 text-xs text-[#0B192C]">{t("co.depositNote", { deposit: fmtUSD(charge), balance: fmtUSD(b.amount - charge) })}</p>}
            {returned && b.status === "pending_payment" ? (
              <p data-testid="checkout-confirming-note" className="mt-4 flex items-start gap-2 rounded-md bg-sky-50 border border-sky-200 px-4 py-3 text-xs text-sky-800">
                {confirming && <Loader2 className="h-4 w-4 shrink-0 animate-spin" />}{confirming ? t("co.confirming") : t("co.confirmSlow")}
              </p>
            ) : live ? (
              <p data-testid="checkout-live-note" className="mt-4 text-sm text-slate-600">{t("co.liveNote")}</p>
            ) : (
              <p data-testid="checkout-mock-note" className="mt-4 rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-xs text-amber-800">{t("co.mock")}</p>
            )}
          </div>
        ) : (
          <p className="text-center text-slate-600">{t("co.other")}</p>
        )}
        {summary}
        {b.status === "pending_payment" && (
          <button onClick={pay} disabled={paying} data-testid="checkout-pay-btn"
            className="mt-8 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#D4AF37] py-4 text-sm font-semibold text-[#0B192C] transition-colors hover:bg-[#F3E5AB] disabled:opacity-60">
            {paying && <Loader2 className="h-4 w-4 animate-spin" />}{paying ? t("co.paying") : returned ? t("co.retry") : t(isDeposit ? "co.payDeposit" : "co.pay", { amount: fmtUSD(charge) })}
          </button>
        )}
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center text-sm">
          <Link to="/" data-testid="checkout-home-link" className="text-center rounded-full border border-slate-300 px-5 py-2.5 hover:border-[#0B192C] transition-colors">{t("co.home")}</Link>
          <Link to="/yachts" className="text-center rounded-full border border-slate-300 px-5 py-2.5 hover:border-[#0B192C] transition-colors">{t("co.yacht")}</Link>
        </div>
      </div>
    </div>
  );
}
